// A polygon is an array of {x, y, cut}: `cut` on vertex i is the id of the cut
// line that edge i -> i+1 lies on, or FRAME for the edge of the art's frame.

export const FRAME = -1;

export const mulberry32 = seed => () => {
	seed = (seed + 0x6d2b79f5) | 0;
	let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
	t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
	return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const keyOf = v => Math.round(v * 256);

const hashPoints = (a, b, salt) => {
	let h = Math.imul(keyOf(a.x) ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(keyOf(a.y) + salt, 0xc2b2ae35);
	h = Math.imul(h ^ (h >>> 16), 0x27d4eb2d) ^ Math.imul(keyOf(b.x), 0x165667b1) ^ Math.imul(keyOf(b.y), 0x9e3779b1);
	h ^= h >>> 15;
	return h >>> 0;
};

export const area = poly => {
	let sum = 0;
	for (let i = 0, n = poly.length; i < n; i++) {
		const a = poly[i];
		const b = poly[(i + 1) % n];
		sum += a.x * b.y - b.x * a.y;
	}
	return sum / 2;
};

export const frameRect = (w, h) => [
	{x: 0, y: 0, cut: FRAME},
	{x: w, y: 0, cut: FRAME},
	{x: w, y: h, cut: FRAME},
	{x: 0, y: h, cut: FRAME},
];

const signedDistance = (line, v) => (v.x - line.px) * line.nx + (v.y - line.py) * line.ny;

const dropDuplicates = poly => {
	const out = [];
	for (let i = 0; i < poly.length; i++) {
		const v = poly[i];
		const next = poly[(i + 1) % poly.length];
		if (Math.abs(v.x - next.x) < 1e-7 && Math.abs(v.y - next.y) < 1e-7 && poly.length - (i - out.length) > 3) continue;
		out.push(v);
	}
	return out;
};

// Both sides compute the same intersection points, so the halves meet exactly.
export const clipHalf = (poly, line, side) => {
	const out = [];
	for (let i = 0, n = poly.length; i < n; i++) {
		const a = poly[i];
		const b = poly[(i + 1) % n];
		const da = signedDistance(line, a);
		const db = signedDistance(line, b);
		const aIn = side * da <= 0;
		const bIn = side * db <= 0;
		if (aIn) out.push({x: a.x, y: a.y, cut: a.cut});
		if (aIn !== bIn) {
			const t = da / (da - db);
			out.push({x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, cut: aIn ? line.id : a.cut});
		}
	}
	return out.length >= 3 ? dropDuplicates(out) : null;
};

export const splitCells = (cells, lines, minArea = 1) => {
	for (const line of lines) {
		const next = [];
		for (const cell of cells) {
			for (const side of [-1, 1]) {
				const half = clipHalf(cell.poly, line, side);
				if (half && area(half) > minArea) next.push({poly: half, sides: {...cell.sides, [line.id]: side}});
			}
		}
		cells = next;
	}
	return cells;
};

const smooth = t => t * t * (3 - 2 * t);

// Built from the stretch's ends in a fixed order, so both cells sharing it get the same points.
export const raggedStretch = (a, b, amplitude, spacing, salt = 0) => {
	const swap = keyOf(a.x) > keyOf(b.x) || (keyOf(a.x) === keyOf(b.x) && keyOf(a.y) > keyOf(b.y));
	const p = swap ? b : a;
	const q = swap ? a : b;
	const dx = q.x - p.x;
	const dy = q.y - p.y;
	const length = Math.sqrt(dx * dx + dy * dy);
	const steps = Math.max(1, Math.round(length / spacing));
	const random = mulberry32(hashPoints(p, q, salt));
	const broadSteps = Math.max(1, Math.round(steps / 3));
	const broad = [];
	for (let i = 0; i <= broadSteps; i++) broad.push(random() * 2 - 1);
	const nx = -dy / (length || 1);
	const ny = dx / (length || 1);
	const ramp = Math.min(length / 2, spacing * 2);
	const points = [];
	for (let i = 1; i < steps; i++) {
		const t = i / steps;
		const edge = Math.min(t * length, (1 - t) * length) / (ramp || 1);
		const k = t * broadSteps;
		const k0 = Math.floor(k);
		const f = smooth(k - k0);
		const bump = broad[k0] * (1 - f) + broad[Math.min(broadSteps, k0 + 1)] * f;
		const offset = amplitude * smooth(Math.min(1, edge)) * (bump * 0.75 + (random() * 2 - 1) * 0.4);
		points.push({x: p.x + dx * t + nx * offset, y: p.y + dy * t + ny * offset});
	}
	if (swap) points.reverse();
	return points;
};

export const raggedPolygon = (poly, amplitudeOf, spacing, salt = 0) => {
	const out = [];
	for (let i = 0, n = poly.length; i < n; i++) {
		const a = poly[i];
		out.push(a);
		const amplitude = a.cut === FRAME ? 0 : amplitudeOf(a.cut);
		if (!amplitude) continue;
		const b = poly[(i + 1) % n];
		for (const point of raggedStretch(a, b, amplitude, spacing, salt)) out.push({x: point.x, y: point.y, cut: a.cut});
	}
	return out;
};

export const insetCell = (cell, lines, widthOf) => {
	let poly = cell.poly;
	for (const line of lines) {
		const side = cell.sides[line.id];
		if (!side) continue;
		const width = widthOf(line.id);
		poly = clipHalf(poly, {id: line.id, px: line.px - side * width * line.nx, py: line.py - side * width * line.ny, nx: line.nx, ny: line.ny}, side);
		if (!poly) return null;
	}
	return area(poly) > 4 ? poly : null;
};

export const clipToRect = (poly, x0, y0, x1, y1) => {
	const edges = [
		{id: FRAME, px: x0, py: y0, nx: -1, ny: 0},
		{id: FRAME, px: x0, py: y0, nx: 0, ny: -1},
		{id: FRAME, px: x1, py: y1, nx: 1, ny: 0},
		{id: FRAME, px: x1, py: y1, nx: 0, ny: 1},
	];
	for (const edge of edges) {
		if (!poly) return null;
		poly = clipHalf(poly, edge, 1);
	}
	return poly;
};

export const polylineLength = points => {
	let sum = 0;
	for (let i = 1; i < points.length; i++) sum += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
	return sum;
};

export const pointAlong = (points, t) => {
	const target = polylineLength(points) * t;
	let walked = 0;
	for (let i = 1; i < points.length; i++) {
		const a = points[i - 1];
		const b = points[i];
		const length = Math.hypot(b.x - a.x, b.y - a.y);
		if (walked + length >= target || i === points.length - 1) {
			const f = length ? Math.min(1, (target - walked) / length) : 0;
			return {x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, tx: (b.x - a.x) / (length || 1), ty: (b.y - a.y) / (length || 1)};
		}
		walked += length;
	}
	return {x: points[0].x, y: points[0].y, tx: 1, ty: 0};
};

export const artRuns = (points, alphaAt, threshold = 0.5) => {
	const runs = [];
	let run = null;
	const inside = p => alphaAt(p.x, p.y) > threshold;
	const edge = (a, b, aIn) => {
		let lo = 0;
		let hi = 1;
		while ((hi - lo) * Math.hypot(b.x - a.x, b.y - a.y) > 0.5) {
			const mid = (lo + hi) / 2;
			const p = {x: a.x + (b.x - a.x) * mid, y: a.y + (b.y - a.y) * mid};
			if (inside(p) === aIn) lo = mid;
			else hi = mid;
		}
		const t = (lo + hi) / 2;
		return {x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t};
	};
	const dense = [];
	for (let i = 0; i < points.length - 1; i++) {
		const a = points[i];
		const b = points[i + 1];
		const steps = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 2));
		for (let s = 0; s < steps; s++) dense.push({x: a.x + (b.x - a.x) * s / steps, y: a.y + (b.y - a.y) * s / steps});
	}
	dense.push(points[points.length - 1]);
	for (let i = 0; i < dense.length; i++) {
		const p = dense[i];
		const pIn = inside(p);
		if (pIn && !run) {
			run = i === 0 ? [p] : [edge(dense[i - 1], p, false), p];
		} else if (pIn) {
			run.push(p);
		} else if (run) {
			run.push(edge(dense[i - 1], p, true));
			runs.push(run);
			run = null;
		}
	}
	if (run && run.length > 1) runs.push(run);
	return runs.filter(r => polylineLength(r) > 1.5);
};

// Inward is to the right of travel.
export const cutPolylines = poly => {
	const n = poly.length;
	let start = poly.findIndex((v, i) => v.cut !== poly[(i + n - 1) % n].cut);
	if (start < 0) start = 0;
	const lines = [];
	let current = null;
	for (let k = 0; k < n; k++) {
		const i = (start + k) % n;
		const v = poly[i];
		const next = poly[(i + 1) % n];
		if (v.cut === FRAME) {
			current = null;
			continue;
		}
		if (!current || current.cut !== v.cut) {
			current = {cut: v.cut, points: [v]};
			lines.push(current);
		}
		current.points.push(next);
	}
	return lines;
};

export const pointInPolygon = (poly, x, y) => {
	let inside = false;
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		const a = poly[i];
		const b = poly[j];
		if ((a.y > y) !== (b.y > y) && x < (b.x - a.x) * (y - a.y) / (b.y - a.y) + a.x) inside = !inside;
	}
	return inside;
};

export const boneAxis = (alpha, w, h) => {
	let count = 0;
	let sx = 0;
	let sy = 0;
	for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
		if (alpha[(y * w + x) * 4 + 3] < 128) continue;
		count++;
		sx += x + 0.5;
		sy += y + 0.5;
	}
	if (!count) return null;
	const cx = sx / count;
	const cy = sy / count;
	let xx = 0;
	let xy = 0;
	let yy = 0;
	for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
		if (alpha[(y * w + x) * 4 + 3] < 128) continue;
		const dx = x + 0.5 - cx;
		const dy = y + 0.5 - cy;
		xx += dx * dx;
		xy += dx * dy;
		yy += dy * dy;
	}
	const angle = 0.5 * Math.atan2(2 * xy, xx - yy);
	const ux = Math.cos(angle);
	const uy = Math.sin(angle);
	let min = Infinity;
	let max = -Infinity;
	let spread = 0;
	for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
		if (alpha[(y * w + x) * 4 + 3] < 128) continue;
		const dx = x + 0.5 - cx;
		const dy = y + 0.5 - cy;
		const along = dx * ux + dy * uy;
		min = Math.min(min, along);
		max = Math.max(max, along);
		spread += Math.abs(-dx * uy + dy * ux);
	}
	return {cx, cy, ux, uy, min, max, thickness: 4 * spread / count};
};

export const boneCrossing = (bone, a, b, endMargin = 0.12) => {
	const dx = b.x - a.x;
	const dy = b.y - a.y;
	const det = bone.ux * dy - bone.uy * dx;
	if (Math.abs(det) < 1e-6) return null;
	const ox = a.x - bone.cx;
	const oy = a.y - bone.cy;
	const along = (ox * dy - oy * dx) / det;
	const t = (ox * bone.uy - oy * bone.ux) / det;
	const span = bone.max - bone.min;
	if (t < 0 || t > 1 || along < bone.min + span * endMargin || along > bone.max - span * endMargin) return null;
	return {x: bone.cx + bone.ux * along, y: bone.cy + bone.uy * along, t};
};
